// src/pages/FileViewerPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import FileViewerModal from "../components/FileViewerModal";

/**
 * FileViewerPage – view-only File Vault orchestration.
 *
 * Responsibilities:
 * - Resolve File Vault metadata and short-lived preview/download URLs.
 * - Preserve signed-in and public-share authorization parameters.
 * - Pass a clean, format-aware record into FileViewerModal.
 * - Request a fresh signed inline PDF URL only when PdfViewer's browser
 *   fallback is explicitly selected.
 *
 * Rendering responsibilities:
 * - PDF rendering and PDF controls belong to PdfViewer.
 * - Office metadata and explicit Office aliases are preserved for FileViewerModal's Microsoft Office path.
 * - This page does not create PDF embeds, Office iframes, Blob URLs, or
 *   format-specific viewer HTML.
 * - Below 1024px, the route header/back button and nested page padding are
 *   removed so FileViewerModal can use the full mobile/tablet viewport.
 */

const API_HOST = process.env.REACT_APP_BACKEND_URL || "";

/* ------------------------------ URL helpers ------------------------------ */

const trimSlashEnd = (value) =>
  String(value || "").replace(/\/+$/, "");

const trimSlashStart = (value) =>
  String(value || "").replace(/^\/+/, "");

const joinUrl = (base, path) => {
  const normalizedBase = trimSlashEnd(base || "");
  const normalizedPath = trimSlashStart(path || "");

  return normalizedBase
    ? `${normalizedBase}/${normalizedPath}`
    : `/${normalizedPath}`;
};

const unique = (items) => Array.from(new Set(items));

const FV_BASE = trimSlashEnd(API_HOST);

function getBaseOrigin() {
  try {
    return new URL(
      FV_BASE || window.location.origin
    ).origin;
  } catch {
    return window.location.origin;
  }
}

const BASE_ORIGIN = getBaseOrigin();

const REQUEST_BASES = unique([
  FV_BASE,
  "",
  "/api",
]).map((base) => trimSlashEnd(base));

function isHttpUrl(value = "") {
  return /^https?:\/\//i.test(String(value || ""));
}

function absolutize(value = "") {
  if (!value) return "";

  const source = String(value);

  if (isHttpUrl(source)) return source;
  if (source.startsWith("//")) return `https:${source}`;

  return joinUrl(FV_BASE || BASE_ORIGIN, source);
}

function shouldAttachShareQuery(urlObject) {
  try {
    const applicationOrigin = new URL(
      FV_BASE || BASE_ORIGIN
    );

    return urlObject.origin === applicationOrigin.origin;
  } catch {
    return false;
  }
}

function withHostAndShareQuery(
  value = "",
  sourceParams
) {
  if (!value) return "";

  const absolute = absolutize(value);
  let urlObject;

  try {
    urlObject = new URL(absolute);
  } catch {
    return absolute;
  }

  // Never append File Vault query values to third-party signed URLs such as
  // Google Cloud Storage URLs. Only same-origin/backend URLs receive them.
  if (shouldAttachShareQuery(urlObject)) {
    const keys = [
      "owner",
      "ownerEmail",
      "nodeId",
      "kind",
      "token",
      "dl",
    ];

    keys.forEach((key) => {
      const incoming = sourceParams?.get(key);

      if (
        incoming &&
        !urlObject.searchParams.get(key)
      ) {
        urlObject.searchParams.set(key, incoming);
      }
    });
  }

  return urlObject.toString();
}

function normalizeKind(value = "") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  return normalized === "files"
    ? "file"
    : normalized;
}

function copyQueryParams(value) {
  return new URLSearchParams(
    String(value || "").replace(/^\?/, "")
  );
}

function ownerFromParams(params) {
  return String(
    params?.get("owner") ||
      params?.get("ownerEmail") ||
      ""
  ).trim();
}

/* -------------------------- Authentication helpers ----------------------- */

async function getIdToken() {
  try {
    const user = getAuth()?.currentUser;

    if (!user) return "";

    return (await user.getIdToken(false)) || "";
  } catch {
    return "";
  }
}

async function authJson(method, url, body) {
  const idToken = await getIdToken();

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(idToken
        ? { Authorization: `Bearer ${idToken}` }
        : {}),
    },
    body: body
      ? JSON.stringify(body)
      : undefined,
    credentials: "include",
  });

  const data = await response
    .json()
    .catch(() => ({}));

  if (!response.ok) {
    const error = new Error(
      data?.error?.message ||
        data?.error ||
        data?.message ||
        `Request failed (${response.status})`
    );

    error.status = response.status;
    error.code =
      data?.code ||
      data?.error?.code ||
      "";
    error.payload = data;

    throw error;
  }

  return data;
}

/* ----------------------------- Type detectors ---------------------------- */

function isPdfName(value = "") {
  return /\.pdf$/i.test(
    String(value || "").split(/[?#]/)[0]
  );
}

function isPdfContentType(value = "") {
  return /application\/pdf/i.test(
    String(value || "")
  );
}

function isPdfUrl(value = "") {
  const source = String(value || "");

  return (
    /\.pdf($|\?)/i.test(source) ||
    /application\/pdf/i.test(source) ||
    /(^|[?&])(contenttype|contentType|response-content-type|mime|mimeType|type)=application\/pdf/i.test(
      source
    )
  );
}

function isOfficeName(value = "") {
  return /\.(docx?|xlsx?|pptx?|rtf)$/i.test(
    String(value || "").split(/[?#]/)[0]
  );
}

function isOfficeContentType(value = "") {
  return /msword|wordprocessingml|spreadsheetml|presentationml|ms-excel|ms-powerpoint|rtf/i.test(
    String(value || "")
  );
}

function isOfficeUrl(value = "") {
  const source = String(value || "");

  return (
    /\.(docx?|xlsx?|pptx?|rtf)(\?|$)/i.test(
      source
    ) ||
    /wordprocessingml|spreadsheetml|presentationml|msword|ms-excel|ms-powerpoint|rtf/i.test(
      source
    )
  );
}

function isImageName(value = "") {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(
    String(value || "").split(/[?#]/)[0]
  );
}

function isImageContentType(value = "") {
  return /^image\//i.test(
    String(value || "")
  );
}

function isImageUrl(value = "") {
  const source = String(value || "");

  return (
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(
      source
    ) ||
    /(^|[?&])(contenttype|contentType|response-content-type|mime|mimeType|type)=image\//i.test(
      source
    )
  );
}

function isHtmlContentType(value = "") {
  return /text\/html|application\/xhtml\+xml/i.test(
    String(value || "")
  );
}

function isTextContentType(value = "") {
  return /^text\//i.test(
    String(value || "")
  );
}

function detectRecordFormat({
  name = "",
  contentType = "",
  urls = [],
  hints = [],
}) {
  const candidates = urls.filter(Boolean);
  const hintText = [
    name,
    contentType,
    ...hints,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    isPdfName(name) ||
    isPdfContentType(contentType) ||
    candidates.some(isPdfUrl) ||
    /(^|[\s._/-])pdf($|[\s._/-])/i.test(hintText)
  ) {
    return "pdf";
  }

  if (
    isOfficeName(name) ||
    isOfficeContentType(contentType) ||
    candidates.some(isOfficeUrl) ||
    /(^|[\s._/-])(docx?|word|xlsx?|excel|pptx?|powerpoint|rtf|office)($|[\s._/-])/i.test(
      hintText
    )
  ) {
    return "office";
  }

  if (
    isImageName(name) ||
    isImageContentType(contentType) ||
    candidates.some(isImageUrl)
  ) {
    return "image";
  }

  if (
    isHtmlContentType(contentType) ||
    /\.html?$/i.test(
      String(name || "").split(/[?#]/)[0]
    )
  ) {
    return "html";
  }

  if (
    isTextContentType(contentType) ||
    /\.(txt|csv|log|md)$/i.test(
      String(name || "").split(/[?#]/)[0]
    )
  ) {
    return "text";
  }

  return "generic";
}

function imageUrlAliases(url, name, contentType) {
  const lowerName = String(name || "").toLowerCase();
  const lowerType = String(contentType || "").toLowerCase();

  return {
    png:
      /\.png$/i.test(lowerName) ||
      /image\/png/i.test(lowerType)
        ? url
        : "",
    jpg:
      /\.(jpe?g)$/i.test(lowerName) ||
      /image\/jpe?g/i.test(lowerType)
        ? url
        : "",
  };
}

/* ---------------------- Preview and download signing --------------------- */

function createVaultParams({
  rawQueryString,
  nodeId,
  kind,
  ownerEmail = "",
}) {
  const params = copyQueryParams(rawQueryString);

  if (nodeId && !params.get("nodeId")) {
    params.set("nodeId", nodeId);
  }

  if (kind && !params.get("kind")) {
    params.set("kind", normalizeKind(kind));
  }

  if (
    ownerEmail &&
    !params.get("owner") &&
    !params.get("ownerEmail")
  ) {
    params.set("owner", ownerEmail);
  }

  return params;
}

async function getPreviewUrl(
  bases,
  nodeId,
  passthroughParams
) {
  const params = new URLSearchParams(
    passthroughParams
  );

  if (!params.get("nodeId")) {
    params.set("nodeId", nodeId);
  }

  const owner = ownerFromParams(params);
  const body = {
    nodeId,
    ...(owner ? { owner } : {}),
  };

  let lastError = null;

  for (const base of bases) {
    const url =
      joinUrl(
        base,
        "vault/transfer/preview-url"
      ) + `?${params.toString()}`;

    try {
      const result = await authJson(
        "POST",
        url,
        body
      );

      if (result?.url) return result;
    } catch (error) {
      lastError = error;
    }
  }

  throw (
    lastError ||
    new Error("Could not obtain preview URL.")
  );
}

async function getPreviewUrlWithRetry(
  bases,
  nodeId,
  passthroughParams
) {
  const maxAttempts = 5;
  let lastError = null;

  for (
    let attempt = 0;
    attempt < maxAttempts;
    attempt += 1
  ) {
    try {
      return await getPreviewUrl(
        bases,
        nodeId,
        passthroughParams
      );
    } catch (error) {
      lastError = error;

      const objectMissing =
        error?.status === 409 &&
        String(error?.code || "") ===
          "gcs_object_missing";

      if (
        !objectMissing ||
        attempt === maxAttempts - 1
      ) {
        throw error;
      }

      const delay = 250 * 2 ** attempt;

      await new Promise((resolve) => {
        window.setTimeout(resolve, delay);
      });
    }
  }

  throw (
    lastError ||
    new Error("Could not obtain preview URL.")
  );
}

async function getDownloadUrl(
  bases,
  nodeId,
  passthroughParams
) {
  const params = new URLSearchParams(
    passthroughParams
  );

  if (!params.get("nodeId")) {
    params.set("nodeId", nodeId);
  }

  const owner = ownerFromParams(params);
  const body = {
    nodeId,
    ...(owner ? { owner } : {}),
  };

  for (const base of bases) {
    const url =
      joinUrl(
        base,
        "vault/transfer/download-url"
      ) + `?${params.toString()}`;

    try {
      const result = await authJson(
        "POST",
        url,
        body
      );

      if (result?.url) return result.url;
    } catch {
      // Download signing is optional for viewing. The inline preview URL is
      // retained as a safe fallback when a separate download URL is unavailable.
    }
  }

  return "";
}

/* -------------------------- Clean record builders ------------------------ */

function readNodePayload(response) {
  let payload = response?.data ?? response ?? {};

  if (payload?.node) {
    payload = payload.node;
  }

  return payload || {};
}

function buildNodeRecord({
  node,
  nodeId,
  kind,
  preview,
  downloadUrl,
  passthroughParams,
}) {
  const title =
    node.title ||
    node.name ||
    node.filename ||
    node.subject ||
    node.displayName ||
    "Document";

  const filename =
    node.filename ||
    node.originalFilename ||
    node.originalName ||
    node.fileName ||
    node.name ||
    node.displayName ||
    title;

  const contentType = String(
    preview?.contentType ||
      preview?.mimeType ||
      node.contentType ||
      node.mimeType ||
      node.mediaType ||
      node.fileMimeType ||
      ""
  ).toLowerCase();

  const formatHints = [
    preview?.kind,
    preview?.format,
    node.format,
    node.fileFormat,
    node.documentFormat,
    node.extension,
    node.fileExtension,
    node.ext,
    node.originalFilename,
    node.originalName,
    node.storagePath,
    node.objectName,
    node.path,
  ].filter(Boolean);

  const previewUrl = withHostAndShareQuery(
    preview?.url || "",
    passthroughParams
  );

  const signedDownloadUrl =
    withHostAndShareQuery(
      downloadUrl || "",
      passthroughParams
    ) || previewUrl;

  const format = detectRecordFormat({
    name: filename,
    contentType,
    urls: [
      previewUrl,
      signedDownloadUrl,
    ],
    hints: formatHints,
  });

  const imageAliases = imageUrlAliases(
    previewUrl,
    filename,
    contentType
  );

  const allowDownloadValue =
    node.allowDownload ??
    node.linkAllowDownload ??
    node.permissions?.download;

  const allowDownload =
    typeof allowDownloadValue === "boolean"
      ? allowDownloadValue
      : undefined;

  return {
    id: node.id || node.nodeId || nodeId,
    nodeId: node.nodeId || node.id || nodeId,
    kind: normalizeKind(kind || node.kind || "file"),
    owner:
      node.owner ||
      node.ownerEmail ||
      ownerFromParams(passthroughParams),
    ownerEmail:
      node.ownerEmail ||
      node.owner ||
      ownerFromParams(passthroughParams),

    subject: title,
    title,
    name: filename,
    filename,
    createdAt:
      node.createdAt ||
      node.updatedAt ||
      node.created ||
      "",
    contentType,

    // Keep explicit HTML only when the backend actually supplied it. Do not
    // manufacture HTML wrappers for PDF, Office, image, or generic files.
    html:
      node.html ||
      node.contentHtml ||
      "",
    htmlDoc:
      node.htmlDoc ||
      "",

    viewerUrl: previewUrl,
    url: previewUrl,
    downloadUrl: signedDownloadUrl,

    pdfUrl:
      format === "pdf"
        ? previewUrl
        : "",
    pdfUrlInline:
      format === "pdf"
        ? previewUrl
        : "",

    urls: {
      pdf:
        format === "pdf"
          ? previewUrl
          : "",
      // FileViewerModal uses this Office alias for Word, Excel, PowerPoint,
      // and RTF previews.
      docx:
        format === "office"
          ? previewUrl
          : "",
      html:
        format === "html"
          ? previewUrl
          : "",
      text:
        format === "text"
          ? previewUrl
          : "",
      png: imageAliases.png,
      jpg: imageAliases.jpg,
    },

    format,
    fileFormat:
      node.fileFormat ||
      node.format ||
      node.extension ||
      "",
    allowDownload,
    linkAllowDownload: allowDownload,
  };
}

async function resolveNode(
  kind,
  nodeId,
  rawQueryString
) {
  const normalizedKind =
    normalizeKind(kind) || "file";

  const initialParams = createVaultParams({
    rawQueryString,
    nodeId,
    kind: normalizedKind,
  });

  const metadataUrls = REQUEST_BASES.map(
    (base) => {
      const params = new URLSearchParams(
        initialParams
      );

      params.set("nodeId", nodeId);

      return (
        joinUrl(base, "vault/nodes/get") +
        `?${params.toString()}`
      );
    }
  );

  let lastError = null;

  for (const metadataUrl of metadataUrls) {
    try {
      const response = await authJson(
        "GET",
        metadataUrl
      );
      const node = readNodePayload(response);

      const passthroughParams =
        createVaultParams({
          rawQueryString,
          nodeId,
          kind: normalizedKind,
          ownerEmail: node.ownerEmail || node.owner || "",
        });

      const preview =
        await getPreviewUrlWithRetry(
          REQUEST_BASES,
          nodeId,
          passthroughParams
        );

      const downloadUrl =
        await getDownloadUrl(
          REQUEST_BASES,
          nodeId,
          passthroughParams
        );

      return buildNodeRecord({
        node,
        nodeId,
        kind: normalizedKind,
        preview,
        downloadUrl,
        passthroughParams,
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(
    lastError?.message ||
      "Could not resolve file for viewing."
  );
}

function enrichRecordUrls(
  initialRecord,
  queryParams
) {
  const record = {
    ...initialRecord,
    urls: {
      ...(initialRecord?.urls || {}),
    },
  };

  [
    "viewerUrl",
    "downloadUrl",
    "url",
    "pdfUrl",
    "pdfUrlInline",
    "fallbackUrl",
    "browserFallbackUrl",
  ].forEach((key) => {
    record[key] = withHostAndShareQuery(
      record[key],
      queryParams
    );
  });

  Object.keys(record.urls).forEach((key) => {
    record.urls[key] =
      withHostAndShareQuery(
        record.urls[key],
        queryParams
      );
  });

  return record;
}

function queryBoolean(
  params,
  key
) {
  const raw = String(
    params.get(key) || ""
  ).trim();

  if (!raw) return undefined;

  return !/^(0|false|no|off)$/i.test(raw);
}

function buildRecordFromQuery(params) {
  const title =
    (params.get("title") || "Document").trim() ||
    "Document";

  const filename =
    (
      params.get("filename") ||
      params.get("name") ||
      title
    ).trim() || title;

  const createdAt =
    params.get("createdAt") || "";

  const contentType =
    params.get("contentType") ||
    params.get("mimeType") ||
    "";

  const html =
    params.get("html") || "";

  // Preserve caller-supplied HTML documents, but never create a wrapper around
  // a direct PDF, Office, image, or generic URL.
  const htmlDoc =
    params.get("htmlDoc") || "";

  const rawUrl =
    params.get("url") || "";
  const rawViewerUrl =
    params.get("viewerUrl") || rawUrl;
  const rawDownloadUrl =
    params.get("downloadUrl") || rawUrl;
  const rawDocxUrl =
    params.get("docxUrl") || "";
  const rawPdfUrl =
    params.get("pdfUrl") || "";
  const rawPdfInline =
    params.get("pdfUrlInline") || "";
  const rawFallbackUrl =
    params.get("browserFallbackUrl") ||
    params.get("fallbackUrl") ||
    "";

  const viewerUrl =
    withHostAndShareQuery(
      rawViewerUrl,
      params
    );
  const downloadUrl =
    withHostAndShareQuery(
      rawDownloadUrl,
      params
    );
  const aliasUrl =
    withHostAndShareQuery(
      rawUrl,
      params
    );
  const docxUrl =
    withHostAndShareQuery(
      rawDocxUrl,
      params
    );
  const pdfUrl =
    withHostAndShareQuery(
      rawPdfUrl,
      params
    );
  const pdfUrlInline =
    withHostAndShareQuery(
      rawPdfInline,
      params
    );
  const browserFallbackUrl =
    withHostAndShareQuery(
      rawFallbackUrl,
      params
    );

  const format = detectRecordFormat({
    name: filename,
    contentType,
    urls: [
      pdfUrl,
      pdfUrlInline,
      docxUrl,
      viewerUrl,
      downloadUrl,
      aliasUrl,
    ],
    hints: [
      params.get("format"),
      params.get("fileFormat"),
      params.get("documentFormat"),
      params.get("extension"),
      params.get("fileExtension"),
    ],
  });

  const primaryUrl =
    viewerUrl ||
    aliasUrl ||
    downloadUrl;

  const imageAliases = imageUrlAliases(
    primaryUrl,
    filename,
    contentType
  );

  const allowDownload =
    queryBoolean(params, "dl") ??
    queryBoolean(params, "allowDownload");

  return {
    id: params.get("id") || params.get("nodeId") || "",
    nodeId: params.get("nodeId") || "",
    kind:
      normalizeKind(params.get("kind")) ||
      "file",
    owner:
      params.get("owner") ||
      params.get("ownerEmail") ||
      "",
    ownerEmail:
      params.get("ownerEmail") ||
      params.get("owner") ||
      "",

    subject: title,
    title,
    name: filename,
    filename,
    createdAt,
    contentType,

    html,
    htmlDoc,
    viewerUrl,
    downloadUrl,
    url: aliasUrl || primaryUrl,

    pdfUrl:
      pdfUrl ||
      pdfUrlInline ||
      (format === "pdf"
        ? primaryUrl
        : ""),
    pdfUrlInline:
      pdfUrlInline ||
      pdfUrl ||
      (format === "pdf"
        ? primaryUrl
        : ""),

    urls: {
      pdf:
        pdfUrl ||
        pdfUrlInline ||
        (format === "pdf"
          ? primaryUrl
          : ""),
      docx:
        docxUrl ||
        (format === "office"
          ? primaryUrl
          : ""),
      html:
        format === "html"
          ? primaryUrl
          : "",
      text:
        format === "text"
          ? primaryUrl
          : "",
      png: imageAliases.png,
      jpg: imageAliases.jpg,
    },

    browserFallbackUrl,
    fallbackUrl: browserFallbackUrl,
    format,
    fileFormat:
      params.get("fileFormat") ||
      params.get("format") ||
      params.get("extension") ||
      "",
    allowDownload,
    linkAllowDownload: allowDownload,
  };
}

/* ---------------------------------- Page --------------------------------- */

const FileViewerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () =>
      new URLSearchParams(
        location.search
      ),
    [location.search]
  );

  const fromApp =
    Boolean(
      location.state?.fromApp
    ) ||
    /^(1|true|yes)$/i.test(
      String(params.get("fromApp") || "")
    );

  const nodeId =
    params.get("nodeId") || "";

  const effectiveKind =
    normalizeKind(params.get("kind")) ||
    (nodeId ? "file" : "");

  const hideBackParam = String(
    params.get("hideBack") || ""
  ).toLowerCase();

  const hideBackViaQuery =
    hideBackParam === "1" ||
    hideBackParam === "true" ||
    hideBackParam === "yes";

  const hideBack =
    fromApp
      ? false
      : hideBackViaQuery;

  const hasDirectInputs = Boolean(
    params.get("html") ||
      params.get("htmlDoc") ||
      params.get("viewerUrl") ||
      params.get("downloadUrl") ||
      params.get("docxUrl") ||
      params.get("pdfUrl") ||
      params.get("pdfUrlInline") ||
      params.get("url")
  );

  const directRecord = useMemo(
    () =>
      hasDirectInputs
        ? buildRecordFromQuery(params)
        : null,
    [hasDirectInputs, params]
  );

  const [loading, setLoading] =
    useState(
      !hasDirectInputs &&
        Boolean(nodeId && effectiveKind)
    );

  const [error, setError] =
    useState("");

  const [record, setRecord] =
    useState(directRecord);

  // Restore the title that existed before this route took ownership.
  useEffect(() => {
    const previousTitle =
      document.title;

    return () => {
      document.title =
        previousTitle;
    };
  }, []);

  // Direct records and File Vault records use one route-aware state effect.
  // This fixes stale records when the URL changes without remounting the page.
  useEffect(() => {
    let cancelled = false;

    if (hasDirectInputs) {
      setRecord(directRecord);
      setError("");
      setLoading(false);

      return () => {
        cancelled = true;
      };
    }

    if (!nodeId || !effectiveKind) {
      setRecord(null);
      setError("");
      setLoading(false);

      return () => {
        cancelled = true;
      };
    }

    setRecord(null);
    setError("");
    setLoading(true);

    (async () => {
      try {
        const resolved =
          await resolveNode(
            effectiveKind,
            nodeId,
            location.search
          );

        if (cancelled) return;

        setRecord(resolved);
      } catch (resolveError) {
        if (cancelled) return;

        setError(
          resolveError?.message ||
            "Failed to open file."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasDirectInputs,
    directRecord,
    nodeId,
    effectiveKind,
    location.search,
  ]);

  useEffect(() => {
    const nextTitle =
      record?.subject ||
      record?.title ||
      directRecord?.subject ||
      directRecord?.title ||
      "Document";

    document.title = nextTitle;
  }, [record, directRecord]);

  const displayRecord = useMemo(
    () =>
      record
        ? enrichRecordUrls(
            record,
            params
          )
        : null,
    [record, params]
  );

  // PdfViewer invokes this callback only after the user selects
  // "Open with browser." A new inline preview URL is signed at that moment,
  // avoiding stale links and avoiding protected backend URLs that require
  // an Authorization header in a new tab.
  const getFreshPdfPreviewUrl =
    useCallback(async () => {
      if (!nodeId) {
        const explicitFallback =
          directRecord?.browserFallbackUrl ||
          directRecord?.fallbackUrl;

        if (explicitFallback) {
          return explicitFallback;
        }

        // Direct records normally contain a public or already-signed URL.
        // PdfViewer can also recover from the PDF bytes it has already loaded.
        return (
          directRecord?.pdfUrlInline ||
          directRecord?.pdfUrl ||
          directRecord?.urls?.pdf ||
          directRecord?.viewerUrl ||
          directRecord?.url ||
          ""
        );
      }

      const passthroughParams =
        createVaultParams({
          rawQueryString:
            location.search,
          nodeId,
          kind:
            effectiveKind || "file",
          ownerEmail:
            displayRecord?.ownerEmail ||
            displayRecord?.owner ||
            "",
        });

      const freshPreview =
        await getPreviewUrlWithRetry(
          REQUEST_BASES,
          nodeId,
          passthroughParams
        );

      if (!freshPreview?.url) {
        throw new Error(
          "The backend did not return a fresh PDF preview URL."
        );
      }

      return freshPreview.url;
    }, [
      nodeId,
      effectiveKind,
      location.search,
      displayRecord,
      directRecord,
    ]);

  const styles = useMemo(
    () => ({
      page: {
        width: "100%",
        maxWidth: 1120,
        margin: "0 auto",
        padding: "18px 16px 44px",
        background: "#F5F7FA",
        boxSizing: "border-box",
      },
      shell: {
        width: "100%",
        maxWidth: "none",
        margin: "12px 0 0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        background: "#fff",
        boxShadow:
          "0 10px 30px rgba(0,0,0,0.08)",
      },
      header: {
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: hideBack
          ? "none"
          : "block",
        padding: 12,
        borderBottom:
          "1px solid #e5e7eb",
        background: "#ffffff",
      },
      headerInner: {
        width: "100%",
        margin: 0,
        display: "flex",
        alignItems: "center",
        justifyContent:
          "space-between",
        gap: 12,
        flexWrap: "wrap",
      },
      left: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      },
      right: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      },
      back: {
        padding: "8px 12px",
        border:
          "1px solid #d1d5db",
        borderRadius: 10,
        background: "#fff",
        cursor: "pointer",
        fontWeight: 700,
      },
      body: {
        minHeight: 0,
        flex: "1 1 auto",
        padding: 16,
        paddingBottom: 24,
        overflow: "hidden",
      },
      spacer: {
        height: 8,
      },
      embeddedCss: `
        .viewer-embed {
          min-width: 0;
          min-height: 0;
        }

        .viewer-embed [role="dialog"][aria-label="Document Viewer"] {
          position: static !important;
          inset: auto !important;
          z-index: auto !important;
          padding: 0 !important;
          background: transparent !important;
        }

        .viewer-embed [role="dialog"][aria-label="Document Viewer"] > div {
          position: relative !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          height: 78vh !important;
          max-width: none !important;
          max-height: none !important;
          border-radius: 12px !important;
          box-shadow: none !important;
        }

        /*
         * Contact-page responsive rule:
         * <= 1024px = mobile/tablet
         * > 1024px = laptop/desktop
         *
         * The outer width, left/right edge, and top starting point intentionally
         * match DocViewerPage one-to-one:
         * - desktop: 1120px max width, 16px sides, viewer starts at 30px
         * - mobile: 12px sides, viewer starts at 26px
         */
        @media (max-width: 1024px) {
          .file-viewer-page {
            width: 100% !important;
            max-width: 1120px !important;
            margin: 0 auto !important;
            padding: 14px 12px 34px !important;
            background: #F5F7FA !important;
            box-sizing: border-box !important;
          }

          .viewer-shell {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            height: auto !important;
            max-height: none !important;
            margin: 12px 0 0 !important;
          }

          .viewer-body {
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            flex: 1 1 auto !important;
            padding: 12px 12px 14px !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          .viewer-embed {
            width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
          }

          .viewer-embed [role="dialog"][aria-label="Document Viewer"] {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }

          .viewer-embed [role="dialog"][aria-label="Document Viewer"] > div {
            width: 100% !important;
            height: min(68dvh, 700px) !important;
            max-height: calc(100dvh - 150px) !important;
            min-height: 420px !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 12px !important;
            overflow: hidden !important;
          }

          .file-viewer-page-tip {
            display: block !important;
            margin: 10px 4px 0 !important;
            padding: 0 4px !important;
          }

          .file-viewer-page-spacer {
            display: block !important;
            height: 8px !important;
          }
        }
      `,
      error: {
        margin: 12,
        padding: 10,
        border:
          "1px solid #fecaca",
        borderRadius: 10,
        background: "#fee2e2",
        color: "#b91c1c",
      },
      loading: {
        padding: 24,
        color: "#6b7280",
        textAlign: "center",
      },
      empty: {
        padding: 24,
        color: "#6b7280",
        fontSize: 14,
      },
      tip: {
        display: hideBack
          ? "none"
          : "block",
        marginTop: 8,
        marginBottom: 0,
        color: "#6b7280",
        textAlign: "center",
        fontSize: 12,
      },
    }),
    [hideBack]
  );

  return (
    <div
      className="file-viewer-page"
      style={styles.page}
    >
      <style>{styles.embeddedCss}</style>

      <div
        className="viewer-shell"
        style={styles.shell}
      >
        {error ? (
          <div
            role="alert"
            style={styles.error}
          >
            {error}
          </div>
        ) : null}

        <div
          className="viewer-body"
          style={styles.body}
        >
          {loading ? (
            <div
              className="screen-only"
              style={styles.loading}
            >
              Loading…
            </div>
          ) : displayRecord ? (
            <div className="viewer-embed">
              <FileViewerModal
                open={true}
                onClose={() =>
                  navigate(-1)
                }
                record={displayRecord}
                fromApp={Boolean(fromApp)}
                getBrowserFallbackUrl={
                  getFreshPdfPreviewUrl
                }
              />
            </div>
          ) : !error ? (
            <div style={styles.empty}>
              No file selected.
            </div>
          ) : null}

          <div
            className="screen-only file-viewer-page-tip"
            style={styles.tip}
          >
            PDF tools are available inside the PDF Viewer. Word and TXT use
            the File Viewer controls.
          </div>

          <div
            className="file-viewer-page-spacer"
            style={styles.spacer}
          />
        </div>
      </div>
    </div>
  );
};

export default FileViewerPage;
